/**
 * MCP Page — Server Integration Status
 *
 * Displays MCP server status, connections, tools, and resources.
 * Data from GET /api/v1/impact-core/mcp/status
 */

import { useQuery } from '@tanstack/react-query';
import {
  Server,
  Plug,
  Wrench,
  Database,
  Activity,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useTranslation } from '@/i18n';
import type { MCPStatus } from '@/types';

const STATE_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'default' | 'muted'> = {
  running: 'success',
  connected: 'success',
  healthy: 'success',
  degraded: 'warning',
  not_configured: 'muted',
  error: 'danger',
  disconnected: 'danger',
};

export function MCPPage() {
  const { t } = useTranslation();
  const statusQuery = useQuery<MCPStatus>({
    queryKey: ['ic', 'mcp', 'status'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/mcp/status');
      return res.data?.data ?? res.data;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const status = statusQuery.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">{t('pages.mcpTitle')}</h1>
          <p className="text-xs text-ic-text-tertiary">Model Context Protocol server status and configuration</p>
        </div>
        <button
          onClick={() => statusQuery.refetch()}
          disabled={statusQuery.isFetching}
          className="flex items-center gap-1.5 rounded-md border border-ic-border px-3 py-2 text-xs font-medium text-ic-text-secondary hover:border-ic-accent/50 hover:text-ic-accent transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${statusQuery.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Loading */}
      {statusQuery.isLoading && (
        <div className="space-y-4">
          <div className="h-32 animate-pulse rounded-lg bg-slate-700/30" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-lg bg-slate-700/30" />
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {statusQuery.isError && (
        <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-6 w-6 text-red-400 mb-2" />
            <p className="text-xs text-red-400">Failed to load MCP status.</p>
            <button
              onClick={() => statusQuery.refetch()}
              className="mt-2 text-xs text-ic-accent hover:underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Status Content */}
      {status && (
        <>
          {/* Main Status Card */}
          <div className="rounded-lg border border-ic-border bg-ic-card p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`flex h-12 w-12 items-center justify-center rounded-full ${
                  status.mcp_enabled ? 'bg-emerald-500/10' : 'bg-slate-500/10'
                }`}>
                  <Server className={`h-6 w-6 ${status.mcp_enabled ? 'text-emerald-400' : 'text-ic-text-tertiary'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-sm font-semibold text-ic-text-primary">{t('pages.mcpTitle')}</h2>
                    <StatusBadge
                      label={status.mcp_enabled ? 'Enabled' : 'Disabled'}
                      variant={status.mcp_enabled ? 'success' : 'muted'}
                      size="md"
                    />
                    <StatusBadge
                      label={status.system_state}
                      variant={STATE_VARIANTS[status.system_state] ?? 'default'}
                      size="md"
                    />
                  </div>
                  <p className="mt-1 text-xs text-ic-text-tertiary">{status.message}</p>
                </div>
              </div>
              {status.checked_at && (
                <span className="text-[10px] text-ic-text-tertiary">
                  Last checked: {new Date(status.checked_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>

          {/* Metric Cards */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <MetricCard
              icon={<Plug className="h-4 w-4" />}
              label="Active Connections"
              value={status.active_connections}
              variant={status.active_connections > 0 ? 'success' : 'muted'}
            />
            <MetricCard
              icon={<Wrench className="h-4 w-4" />}
              label="Available Tools"
              value={status.available_tools}
              variant={status.available_tools > 0 ? 'info' : 'muted'}
            />
            <MetricCard
              icon={<Database className="h-4 w-4" />}
              label="Available Resources"
              value={status.available_resources}
              variant={status.available_resources > 0 ? 'info' : 'muted'}
            />
            <MetricCard
              icon={<Activity className="h-4 w-4" />}
              label="System State"
              value={status.system_state}
              variant={STATE_VARIANTS[status.system_state] ?? 'default'}
              isText
            />
          </div>

          {/* Configuration Guide (shown when not configured) */}
          {status.system_state === 'not_configured' && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-400 shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-ic-text-primary mb-2">MCP Server Not Configured</h3>
                  <p className="text-xs text-ic-text-secondary mb-4">
                    The MCP server integration has not been configured yet. Follow the steps below to enable it:
                  </p>
                  <ol className="space-y-2 text-xs text-ic-text-secondary list-decimal list-inside">
                    <li>Ensure the MCP server binary is installed and accessible</li>
                    <li>Configure the MCP connection settings in the backend environment</li>
                    <li>Set <code className="rounded bg-slate-700/50 px-1.5 py-0.5 text-[10px] font-mono text-ic-text-primary">MCP_ENABLED=true</code> in the backend configuration</li>
                    <li>Configure tool and resource endpoints as needed</li>
                    <li>Restart the backend service and verify connectivity</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Connected State Details */}
          {status.mcp_enabled && status.system_state !== 'not_configured' && (
            <div className="rounded-lg border border-ic-border bg-ic-card p-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 text-emerald-400 shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-ic-text-primary mb-2">Server Active</h3>
                  <p className="text-xs text-ic-text-secondary">
                    The MCP server is running and accepting connections. Tools and resources are available
                    for AI-assisted operations within Impact Core.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Sub-Components ────────────────────────────────────────────

const VARIANT_COLORS: Record<string, string> = {
  success: 'text-emerald-400',
  info: 'text-blue-400',
  warning: 'text-amber-400',
  danger: 'text-red-400',
  muted: 'text-ic-text-tertiary',
  default: 'text-ic-text-secondary',
};

function MetricCard({
  icon,
  label,
  value,
  variant,
  isText,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  variant: string;
  isText?: boolean;
}) {
  const color = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;

  return (
    <div className="rounded-lg border border-ic-border bg-ic-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-ic-text-tertiary">{icon}</span>
        <span className="text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{label}</span>
      </div>
      <p className={`text-xl font-semibold ${color} ${isText ? 'text-sm capitalize' : ''}`}>
        {isText ? String(value).replace('_', ' ') : value}
      </p>
    </div>
  );
}
